import {z} from 'zod';

import {isNil} from 'ramda';

import {
  TelephonyInboundRecordingNoticeSchema,
  TelephonyOutboundRecordingNoticeSchema,
  TelephonyRecordingSettings,
  UpdateTelephonyRecordingSettingsApiArg,
} from '@dms/api/settings';

/** The backend refuses a longer notice; about 300 characters are already 20 seconds of speech. */
export const RECORDING_NOTICE_MAX_LENGTH = 1000;

export const telephonyRecordingSettingsFormSchema = z.object({
  // Not `.refine(isNotNil)`: a type guard narrows the form type to `number`, but the field is null
  // while the user clears it.
  retentionDays: z
    .number()
    .int()
    .min(1)
    .nullable()
    .refine((days) => !isNil(days)),
  noticeInbound: TelephonyInboundRecordingNoticeSchema,
  noticeOutbound: TelephonyOutboundRecordingNoticeSchema,
  noticeText: z.string().max(RECORDING_NOTICE_MAX_LENGTH).nullable(),
});

export type TelephonyRecordingSettingsFormValues = z.infer<
  typeof telephonyRecordingSettingsFormSchema
>;

export const getTelephonyRecordingSettingsFormValues = (
  settings: TelephonyRecordingSettings
): TelephonyRecordingSettingsFormValues => ({
  retentionDays: settings.retentionDays,
  noticeInbound: settings.noticeInbound,
  noticeOutbound: settings.noticeOutbound,
  noticeText: settings.noticeText,
});

/**
 * Blank text sends null, which the backend stores as the current default text. A direction newly
 * set to `operator` was confirmed in the dialog when it was picked, so the save says so; keeping
 * `operator` needs no new confirmation.
 */
export const getUpdateTelephonyRecordingSettingsBody = (
  values: TelephonyRecordingSettingsFormValues,
  saved: TelephonyRecordingSettings
): UpdateTelephonyRecordingSettingsApiArg['body'] => {
  const noticeText = values.noticeText?.trim() ?? '';

  return {
    retentionDays: values.retentionDays ?? saved.retentionDays,
    noticeInbound: values.noticeInbound,
    noticeOutbound: values.noticeOutbound,
    noticeText: noticeText === '' ? null : noticeText,
    confirmOperatorNotice:
      (values.noticeInbound === 'operator' && saved.noticeInbound !== 'operator') ||
      (values.noticeOutbound === 'operator' && saved.noticeOutbound !== 'operator'),
  };
};
